import { useEffect, useReducer, useCallback, useRef, useMemo } from 'react';
import { useAudioElement } from './useAudioElement';

// --- Reducer for managing player state ---
const playerReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PLAYLIST':
      const newPlaylist = action.payload || [];
      const currentTrackId = state.playlist[state.currentIndex]?.key;
      let nextIndex = -1;

      if (currentTrackId) {
        // Try to find the current track in the new playlist to maintain position
        const foundIndex = newPlaylist.findIndex(track => track.key === currentTrackId);
        if (foundIndex !== -1) {
          nextIndex = foundIndex;
        }
      }

      return {
        ...state,
        playlist: newPlaylist,
        // If the current track is no longer in the playlist, reset.
        currentIndex: nextIndex,
      };
    case 'SET_PLAYLIST_AND_PLAY':
      return {
        ...state,
        playlist: action.payload?.playlist || [],
        currentIndex: Number.isInteger(action.payload?.index) ? action.payload.index : 0,
      };
    case 'PLAY_TRACK':
      // Only update if the index is valid and different
      if (action.payload >= 0 && action.payload < state.playlist.length && action.payload !== state.currentIndex) {
        return { ...state, currentIndex: action.payload };
      }
      return state;
    case 'PLAY_NEXT':
      if (state.currentIndex < state.playlist.length - 1) {
        return { ...state, currentIndex: state.currentIndex + 1 };
      }
      return state;
    case 'PLAY_PREV':
      if (state.currentIndex > 0) {
        return { ...state, currentIndex: state.currentIndex - 1 };
      }
      return state;
    default:
      return state;
  }
};

const initialState = {
  playlist: [],
  currentIndex: -1,
};

export const useAudioPlayer = () => {
  // Core audio state and controls are now managed by useAudioElement
  const { audioRef, dispatch: dispatchAudio, ...audioStateAndControls } = useAudioElement();
  const { play: playAudio, pause: pauseAudio } = audioStateAndControls;

  // Playlist state is now managed by the reducer
  const [state, dispatch] = useReducer(playerReducer, initialState);
  const { playlist, currentIndex } = state;

  const shouldAutoplayRef = useRef(false);
  const isPlayingRef = useRef(false);
  const hasExplicitSelectionRef = useRef(false);
  const candidateLadderRef = useRef([]);
  const candidateIdxRef = useRef(0);

  useEffect(() => {
    isPlayingRef.current = audioStateAndControls.isPlaying;
  }, [audioStateAndControls.isPlaying]);

  // Effect to handle auto-advancing to the next track when one ends
  useEffect(() => {
    const handleEnded = () => {
      // Dispatch an action to play the next track
      if (currentIndex < playlist.length - 1) {
        shouldAutoplayRef.current = true;
        dispatch({ type: 'PLAY_NEXT' });
      } else {
        // If at the end of the playlist, explicitly pause.
        shouldAutoplayRef.current = false;
        pauseAudio();
      }
    };

    const el = audioRef.current;
    el?.addEventListener('ended', handleEnded);
    return () => el?.removeEventListener('ended', handleEnded);
  }, [audioRef, currentIndex, playlist.length, pauseAudio]);

  // removed unused loadIdRef

  // Helper to get MIME type from URL extension
  const getMimeType = (url = '') => {
    if (/\.mp3($|\?)/i.test(url)) return 'audio/mpeg';
    if (/\.ogg($|\?)/i.test(url)) return 'audio/ogg';
    if (/\.wav($|\?)/i.test(url)) return 'audio/wav';
    if (/\.m4a($|\?)/i.test(url)) return 'audio/mp4';
    return ''; // Unknown
  };

  const loadTrack = useCallback((trackIndex, preferredUrl) => {
    if (trackIndex < 0 || trackIndex >= playlist.length) return;
    const audio = audioRef.current;
    const track = playlist[trackIndex];
    const url = preferredUrl || track?.url || (Array.isArray(track?.candidates) && track.candidates[0]);

    if (url && audio.currentSrc !== url && audio.src !== url) {
      dispatchAudio({ type: 'SET_ERROR', payload: null });
      audio.src = url;
      audio.load();
      dispatchAudio({ type: 'SET_LOADING', payload: true });
    }
  }, [playlist, audioRef, dispatchAudio]);

  // Effect to load and play a new track when the currentIndex changes
  useEffect(() => {
    if (currentIndex < 0 || currentIndex >= playlist.length) {
      shouldAutoplayRef.current = false;
      return;
    }
    const audio = audioRef.current;

    const t = playlist[currentIndex] || {};
    const initialLadder = Array.isArray(t.candidates) && t.candidates.length
      ? t.candidates
      : t.url ? [t.url] : [];

    // Pre-filter and sort candidates by browser support
    const ladder = initialLadder
      .map(url => ({
        url,
        supportLevel: audio.canPlayType(getMimeType(url)),
      }))
      .filter(item => item.supportLevel !== '') // Remove unsupported formats
      .sort((a, b) => {
        // Prioritize 'probably' > 'maybe'
        if (a.supportLevel === 'probably' && b.supportLevel !== 'probably') return -1;
        if (b.supportLevel === 'probably' && a.supportLevel !== 'probably') return 1;
        return 0;
      })
      .map(item => item.url);

    if (ladder.length === 0) {
      dispatchAudio({ type: 'SET_ERROR', payload: 'No supported audio formats found for this track.' });
      return;
    }

    candidateLadderRef.current = ladder;
    candidateIdxRef.current = 0;

    loadTrack(currentIndex, ladder[0]);

    if (shouldAutoplayRef.current) {
      playAudio();
      shouldAutoplayRef.current = false;
    }
  }, [currentIndex, playlist, loadTrack, audioRef, dispatchAudio, playAudio]);

  // Try next candidate on error for the current track
  useEffect(() => {
    const audio = audioRef.current;
    const onError = () => {
      const ladder = candidateLadderRef.current || [];
      const nextIdx = candidateIdxRef.current + 1;
      if (nextIdx < ladder.length) {
        candidateIdxRef.current = nextIdx;
        const nextUrl = ladder[nextIdx];
        const resumeAt = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
        const shouldResume = isPlayingRef.current || shouldAutoplayRef.current;
        dispatchAudio({ type: 'SET_ERROR', payload: null });
        audio.src = nextUrl;
        audio.load();
        dispatchAudio({ type: 'SET_LOADING', payload: true });
        const resumePlayback = () => {
          if (resumeAt > 0 && Number.isFinite(audio.duration)) {
            audio.currentTime = Math.min(resumeAt, Math.max(0, audio.duration - 0.5));
          }
          if (shouldResume) {
            playAudio();
          }
        };
        audio.addEventListener('canplay', resumePlayback, { once: true });
        return;
      }
      dispatchAudio({ type: 'SET_ERROR', payload: `Audio failed for all candidates: ${ladder.join(', ')}` });
    };
    audio.addEventListener('error', onError);
    return () => audio.removeEventListener('error', onError);
  }, [audioRef, dispatchAudio, playAudio]);

  // --- Public API for the hook ---

  const setPlaylist = useCallback((newPlaylist) => {
    if (hasExplicitSelectionRef.current) return;
    dispatch({ type: 'SET_PLAYLIST', payload: newPlaylist || [] });
  }, []);

  const playSingleTrack = useCallback((track) => {
    if (!track) return;
    hasExplicitSelectionRef.current = true;
    shouldAutoplayRef.current = true;

    const audio = audioRef.current;
    const initialUrl = track.url || (Array.isArray(track.candidates) && track.candidates[0]);
    if (initialUrl) {
      dispatchAudio({ type: 'SET_ERROR', payload: null });
      if (audio.currentSrc !== initialUrl && audio.src !== initialUrl) {
        audio.src = initialUrl;
        audio.load();
        dispatchAudio({ type: 'SET_LOADING', payload: true });
      }
      playAudio().catch(() => {});
    }

    dispatch({
      type: 'SET_PLAYLIST_AND_PLAY',
      payload: {
        playlist: [track],
        index: 0,
      },
    });
  }, [audioRef, dispatchAudio, playAudio]);

  const selectTrack = useCallback((index, options = {}) => {
    if (index < 0 || index >= playlist.length) {
      shouldAutoplayRef.current = false;
      return;
    }

    const shouldAutoplay = options.autoplay ?? isPlayingRef.current;
    shouldAutoplayRef.current = shouldAutoplay;

    if (index !== currentIndex) {
      dispatch({ type: 'PLAY_TRACK', payload: index });
    } else if (shouldAutoplay && !isPlayingRef.current) {
      playAudio();
      shouldAutoplayRef.current = false;
    }
  }, [currentIndex, playlist.length, playAudio]);

  const playNext = useCallback(() => {
    shouldAutoplayRef.current = true;
    dispatch({ type: 'PLAY_NEXT' });
  }, []);

  const playPrev = useCallback(() => {
    shouldAutoplayRef.current = true;
    dispatch({ type: 'PLAY_PREV' });
  }, []);

  const currentTrack = useMemo(
    () => (currentIndex >= 0 && currentIndex < playlist.length ? playlist[currentIndex] : null),
    [currentIndex, playlist],
  );

  return {
    // Expose all state and controls from the audio element hook
    ...audioStateAndControls,
    // Expose playlist-specific state and controls
    setPlaylist,
    playSingleTrack,
    selectTrack,
    playNext,
    playPrev,
    currentIndex,
    playlist,
    currentTrack,
  };
};
